<script setup lang="ts">
    import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
    import PkHostCallConfirmCard from './PkHostCallConfirmCard.vue'

    /**
     * The confirmation card of a host call, in the top layer: shown while the
     * page that embeds the widget has a modal dialog open (spec
     * `page-tools.md`). That dialog makes everything else inert, the chat and
     * its card included, so the card the agent waits on could not be clicked.
     * A modal dialog of its own, opened last, is the one the page lets the
     * user answer; once answered, the page's dialog is active again.
     */
    defineProps<{
        question: string
        details?: { label: string; value: string; before?: string }[]
        unchanged?: { label: string; value: string }[]
    }>()

    const emit = defineEmits<{
        respond: [approved: boolean]
    }>()

    const dialog = useTemplateRef<HTMLDialogElement>('dialog')

    // Distance kept from the window's edges, in pixels.
    const EDGE = 16

    // The top layer leaves the widget's layout, so the card is placed by
    // measure: over the card the chat shows, where the user looks for it, and
    // it follows that card while the chat still scrolls or settles. Without
    // one in view (a closed panel), at the bottom of the window.
    const anchor = ref<{ left: string; top: string; width: string }>()
    const place = () => {
        const element = dialog.value
        if (!element) {
            return
        }
        const cards = [
            ...(
                element.getRootNode() as Document | ShadowRoot
            ).querySelectorAll<HTMLElement>('.pk-host-call-confirm-card'),
        ].filter((card) => !element.contains(card))
        const rect = cards[cards.length - 1]?.getBoundingClientRect()
        if (!rect || rect.width === 0) {
            anchor.value = undefined
            return
        }
        const top = Math.max(
            EDGE,
            Math.min(
                rect.top,
                window.innerHeight - element.offsetHeight - EDGE,
            ),
        )
        const next = {
            left: `${rect.left}px`,
            top: `${top}px`,
            width: `${rect.width}px`,
        }
        if (JSON.stringify(next) !== JSON.stringify(anchor.value)) {
            anchor.value = next
        }
    }

    let frame = 0
    const follow = () => {
        place()
        frame = requestAnimationFrame(follow)
    }
    onMounted(() => {
        dialog.value?.showModal()
        follow()
    })
    onBeforeUnmount(() => {
        cancelAnimationFrame(frame)
    })
</script>

<template>
    <!-- Escape answers no, as Cancel does -->
    <dialog
        ref="dialog"
        class="pk-host-call-confirm-dialog"
        :class="{ 'pk-host-call-confirm-dialog--anchored': anchor }"
        :style="anchor"
        @cancel.prevent="emit('respond', false)">
        <PkHostCallConfirmCard
            :question="question"
            :details="details"
            :unchanged="unchanged"
            @respond="emit('respond', $event)" />
    </dialog>
</template>

<style lang="scss">
    .pk-host-call-confirm-dialog {
        // The widget's reset leaves a dialog at the top left corner
        position: fixed;
        inset: auto 0 var(--spacing-32);
        width: min(24rem, calc(100vw - var(--spacing-32)));
        max-width: none;
        margin: 0 auto;
        padding: 0;
        border: 0;
        border-radius: var(--rounded-xl);
        background: transparent;
        color: var(--color-word-1);
        font-family: var(--font-sans);
        box-shadow: var(--shadow-xl);

        &--anchored {
            inset: auto;
            margin: 0;
        }

        // The page's own dialog already dims the page behind
        &::backdrop {
            background: transparent;
        }
    }
</style>
